import { useState } from 'react'
import { useAppSelector } from 'hooks/redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import {
  OrderSettingsProvider,
  useOrderSettings,
} from 'components/dme/order/order-settings/useOrderSettings'
import DashboardContent from '../atoms/DashboardContent'
import routes from '../constants/routes'
import Button from 'components/atoms/Button'
import Toast from 'components/atoms/Toast'
import OrderSettings from 'components/dme/order/order-settings/OrderSettings'
import { newPatientInHistorySelector } from 'store/slice/patientSlice'
import { useCreateLabOrderMutation } from 'store/services/labOrder'
import * as generalHelper from 'helpers/generalHelper'
import PatientInformation from './PatientInformation';
import AIRxRecognitionCheckBox from 'components/dme/order/AIRxRecognitionCheckBox';

const DOB_FORMATS = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601]

const NewPatientOrder = () => {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const { state: orderSettings } = useOrderSettings()
  const { officeId, ...newPatient } = useAppSelector(newPatientInHistorySelector) || {}
  const [createOrder] = useCreateLabOrderMutation()
  const [rxRecognitionOrderInput, setRxRecognitionOrderInput] = useState(false)

  const patientDob = moment(newPatient?.dob, DOB_FORMATS, true)
  const patientOverEighteen = patientDob.isValid() && moment().diff(patientDob, 'years') >= 18
  const isReadyToSubmit =
    orderSettings.isValid && newPatient && (patientOverEighteen || orderSettings.testType !== 'HST')
  const isHstPsg = orderSettings?.testType === 'HST' || orderSettings?.testType === 'PSG'

  const onSubmit = async () => {
    try {
      if (newPatient && !submitting) {
        setSubmitting(true)
        const data = {
          patient: {
            ...newPatient,
            dob: patientDob.format('YYYY-MM-DD'),
          },
          tests: Object.values(orderSettings.tests || []).map((conditions, idx) => ({
            ...conditions,
            night: idx + 1,
          })),
          testType: orderSettings.testType,
          officeId,
          rxRecognitionOrderInput,
        }
        const { data: orderCreated } = await createOrder(data)
        console.log('Order Created', orderCreated)
        Toast({ type: 'success', label: 'New order created!' })

        navigate(`${routes.index}${routes.order.edit}${orderCreated.id}?bucket=OPEN`)
      }
    } catch (error: any) {
      console.log(`Error submitting new order and patient info: ${error.message}`)
      Toast({
        type: 'error',
        label: `Error creating new Patient: ${error.message}`,
      })
    }
    setSubmitting(false)
  }

  return (
    <DashboardContent
      title="New Order"
      subtitle="New Patient"
      content={
        <>
          <PatientInformation patientData={newPatient} />
          <OrderSettings officeId={officeId} />
          <div className="d-flex align-items-center justify-content-between">
            <div className="ml-5">
              {!isHstPsg &&
                <AIRxRecognitionCheckBox
                  rxRecognitionOrderInput={rxRecognitionOrderInput}
                  setRxRecognitionOrderInput={setRxRecognitionOrderInput}
                />
              }
            </div>
            <Button
              className="btn btn-danger float-right mr-2 text-nowrap"
              onClick={onSubmit}
              disabled={!isReadyToSubmit || submitting}
              label={generalHelper.t('Create Order')}
            />
            {!patientOverEighteen && orderSettings.testType === 'HST' && (
              <div className="row mt-2">
                <div className="col-sm-12 d-flex justify-content-end">
                  <span className="font-weight-bold text-danger">
                    Cannot order HST if patient is under 18
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      }
      breadcrumb={[
        {
          label: 'New Order',
          to: `${routes.index}${routes.order.newOrder}`,
        },
        {
          label: 'New Patient',
          to: `${routes.index}${routes.order.newOrder}/${routes.patient.new}`,
        },
      ]}
    />
  )
}

const NewPatientOrderWrapper = () => {
  return (
    <OrderSettingsProvider>
      <NewPatientOrder />
    </OrderSettingsProvider>
  )
}

export default NewPatientOrderWrapper
